import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { serverUrl } from "../../../../../nestserver";

export const INQUIRY_QUERY_KEY = "admission-inquiries";

const STATUS_META = {
  new: { label: "New", color: "blue" },
  contacted: { label: "Contacted", color: "orange" },
  visited: { label: "Visited", color: "purple" },
  converted: { label: "Converted", color: "green" },
  closed: { label: "Closed", color: "default" },
};

const SOURCE_LABELS = {
  walk_in: "Walk-in",
  phone: "Phone",
  website: "Website",
  referral: "Referral",
  parent: "Parent",
  other: "Other",
};

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

// Shared by the Senior and Montessori admission screens.
const AdmissionInquiry = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const inquiries = useQuery([INQUIRY_QUERY_KEY, statusFilter, search], () =>
    axios
      .get(`${serverUrl}/admission-inquiry`, {
        params: { status: statusFilter, search: search || undefined },
        headers: authHeaders(),
      })
      .then((res) => res.data)
  );

  const classes = useQuery("classes", () =>
    axios
      .get(`${serverUrl}/classes`, { headers: authHeaders() })
      .then((res) => res.data)
  );

  const refresh = () => queryClient.invalidateQueries(INQUIRY_QUERY_KEY);

  const openModal = (record) => {
    setEditing(record || null);
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        ...record,
        class: record.class?._id,
        dob: record.dob ? moment(record.dob, "YYYY-MM-DD") : undefined,
      });
    } else {
      form.setFieldsValue({ source: "walk_in" });
    }
    setModalOpen(true);
  };

  const onSave = async (values) => {
    const payload = {
      ...values,
      // Plain calendar date — a date object would be serialised as UTC.
      dob: values.dob ? values.dob.format("YYYY-MM-DD") : "",
    };
    setSaving(true);
    try {
      const res = editing
        ? await axios.patch(
            `${serverUrl}/admission-inquiry/${editing._id}`,
            payload,
            { headers: authHeaders() }
          )
        : await axios.post(`${serverUrl}/admission-inquiry`, payload, {
            headers: authHeaders(),
          });
      if (res?.data?.success === false) {
        message.error(res.data.message);
        return;
      }
      message.success(res?.data?.message);
      setModalOpen(false);
      refresh();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Could not save the inquiry"
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await axios.delete(
        `${serverUrl}/admission-inquiry/${record._id}`,
        { headers: authHeaders() }
      );
      if (res?.data?.success === false) {
        message.error(res.data.message);
        return;
      }
      message.success(res?.data?.message);
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.message || "Could not delete");
    }
  };

  const onStatusChange = async (record, status) => {
    try {
      const res = await axios.patch(
        `${serverUrl}/admission-inquiry/${record._id}`,
        { status },
        { headers: authHeaders() }
      );
      if (res?.data?.success === false) {
        message.error(res.data.message);
        return;
      }
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.message || "Could not update status");
    }
  };

  const convert = (record) =>
    navigate(
      `/dashboard/admin/menu/admission?tab=1&inquiry=${record._id}`
    );

  const counts = inquiries.data?.counts || {};

  const columns = [
    {
      title: "Child",
      key: "child",
      render: (_, r) => (
        <div>
          <div className="font-semibold capitalize">
            {r.first_name} {r.last_name}
          </div>
          {r.dob && (
            <div className="text-xs text-gray-500">
              DOB {moment(r.dob, "YYYY-MM-DD").format("DD-MM-YYYY")}
            </div>
          )}
        </div>
      ),
    },
    { title: "Class", key: "class", render: (_, r) => r.class?.name || "-" },
    {
      title: "Parent",
      key: "parent",
      render: (_, r) => (
        <div>
          <div className="capitalize">{r.parent_name}</div>
          <div className="text-xs text-gray-500">{r.phone}</div>
          {r.address && (
            <div className="text-xs text-gray-500">{r.address}</div>
          )}
        </div>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (v) => SOURCE_LABELS[v] || v,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) =>
        r.status === "converted" ? (
          <Tag color={STATUS_META.converted.color}>
            Converted
            {r.application?.application_number
              ? ` · ${r.application.application_number}`
              : ""}
          </Tag>
        ) : (
          <Select
            size="small"
            value={r.status}
            style={{ width: 120 }}
            onChange={(value) => onStatusChange(r, value)}
            options={["new", "contacted", "visited", "closed"].map((key) => ({
              value: key,
              label: STATUS_META[key].label,
            }))}
          />
        ),
    },
    {
      title: "Added",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) =>
        r.status === "converted" ? (
          "-"
        ) : (
          <div className="flex gap-3 items-center">
            {/* A closed (or parent-withdrawn) inquiry is finished: it can
                only be reopened from the status dropdown, or deleted. */}
            {r.status !== "closed" && (
              <>
                <Button type="primary" size="small" onClick={() => convert(r)}>
                  Convert
                </Button>
                <Button type="link" size="small" onClick={() => openModal(r)}>
                  Edit
                </Button>
              </>
            )}
            <Popconfirm
              title="Delete this inquiry?"
              okText="Delete"
              onConfirm={() => onDelete(r)}
            >
              <Button type="link" size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          </div>
        ),
    },
  ];

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-2xl text-orange-500 font-semibold">
            Admission Inquiries
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <Input.Search
              allowClear
              placeholder="Search name or phone"
              style={{ width: 240 }}
              onSearch={setSearch}
            />
            <Select
              allowClear
              placeholder="All statuses"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={Object.keys(STATUS_META).map((key) => ({
                value: key,
                label: `${STATUS_META[key].label}${
                  counts[key] ? ` (${counts[key]})` : ""
                }`,
              }))}
            />
            <Button type="primary" onClick={() => openModal()}>
              Add Inquiry
            </Button>
          </div>
        </div>
        <div className="py-5">
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={inquiries.data?.data || []}
            loading={inquiries.isLoading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      <Modal
        title={editing ? "Edit Inquiry" : "Add Inquiry"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="first_name"
              label="Child First Name"
              rules={[
                { required: true, whitespace: true, message: "Required" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="last_name" label="Child Last Name">
              <Input />
            </Form.Item>
            <Form.Item name="dob" label="Date Of Birth">
              <DatePicker
                className="w-full"
                format="DD-MM-YYYY"
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
              />
            </Form.Item>
            <Form.Item name="gender" label="Gender">
              <Select
                allowClear
                placeholder="Select gender"
                options={[
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "others", label: "Other" },
                ]}
              />
            </Form.Item>
            <Form.Item name="class" label="Class Interested In">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select class"
                options={(classes.data || []).map((c) => ({
                  value: c._id,
                  label: c.name,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="parent_name"
              label="Parent Name"
              rules={[
                { required: true, whitespace: true, message: "Required" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Parent Phone"
              rules={[
                { required: true, message: "Required" },
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Please enter a valid 10-digit phone number!",
                },
              ]}
              getValueFromEvent={(e) => e.target.value.slice(0, 10)}
            >
              <Input inputMode="numeric" maxLength={10} />
            </Form.Item>
            <Form.Item
              name="email"
              label="Parent Email"
              rules={[{ type: "email", message: "Please enter a valid email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="source" label="Source">
              <Select
                options={Object.keys(SOURCE_LABELS).map((key) => ({
                  value: key,
                  label: SOURCE_LABELS[key],
                }))}
              />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Parent Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <div className="flex justify-end gap-3">
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdmissionInquiry;
